import React, { useState } from 'react';
import { ValueMap, isAdvancedValueMap, isSimpleValueMap } from '../../types';

interface GoogleSheetsSnippetProps {
  valueMap: ValueMap;
}

export const GoogleSheetsSnippet: React.FC<GoogleSheetsSnippetProps> = ({ valueMap }) => {
  const [copied, setCopied] = useState(false);

  const generateSnippet = (): string => {
    if (isAdvancedValueMap(valueMap)) {
      // Advanced mode: 13 fields
      const { customerName, businessPriorities, digitalPriorities, externalChallenges, internalChallenges } = valueMap;
      
      const snippet = `|---------------start_copy---------------| ^^ai_customer_name^^${customerName}^^ai_customer_name^^ ^^ai_busi_prior1^^${businessPriorities[0]}^^ai_busi_prior1^^ ^^ai_busi_prior2^^${businessPriorities[1]}^^ai_busi_prior2^^ ^^ai_busi_prior3^^${businessPriorities[2]}^^ai_busi_prior3^^ ^^ai_digital_prior1^^${digitalPriorities[0]}^^ai_digital_prior1^^ ^^ai_digital_prior2^^${digitalPriorities[1]}^^ai_digital_prior2^^ ^^ai_digital_prior3^^${digitalPriorities[2]}^^ai_digital_prior3^^ ^^ai_exter_chall1^^${externalChallenges[0]}^^ai_exter_chall1^^ ^^ai_exter_chall2^^${externalChallenges[1]}^^ai_exter_chall2^^ ^^ai_exter_chall3^^${externalChallenges[2]}^^ai_exter_chall3^^ ^^ai_inter_chall1^^${internalChallenges[0]}^^ai_inter_chall1^^ ^^ai_inter_chall2^^${internalChallenges[1]}^^ai_inter_chall2^^ ^^ai_inter_chall3^^${internalChallenges[2]}^^ai_inter_chall3^^ |---------------end_copy---------------|`;
      
      return snippet;
    } else if (isSimpleValueMap(valueMap)) {
      // Simple mode: 3 fields
      const { businessDriver, painPoint, measurableOutcome } = valueMap;
      
      const snippet = `|---------------start_copy---------------| ^^ai_business_driver^^${businessDriver}^^ai_business_driver^^ ^^ai_pain_point^^${painPoint}^^ai_pain_point^^ ^^ai_measurable_outcome^^${measurableOutcome}^^ai_measurable_outcome^^ |---------------end_copy---------------|`;
      
      return snippet;
    }
    
    return '';
  };

  const handleCopy = async () => {
    const snippet = generateSnippet();
    await navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mt-6 p-6 bg-gray-50 border border-gray-200 rounded-lg">
      <h3 className="text-lg font-bold text-gray-800 mb-3">Google Sheets Integration</h3>
      
      <div className="mb-4 text-sm text-gray-600 space-y-2">
        <p className="font-semibold">Instructions:</p>
        <ol className="list-decimal list-inside space-y-1 ml-2">
          <li>Copy the <strong>SNIPPET</strong> below</li>
          <li>
            Access the{' '}
            <a 
              href="https://docs.google.com/spreadsheets/d/13TTIIdslal7OQaxI_fUfqLDqoKmH858TJHBcXPutO0g/edit?gid=0#gid=0"
              target="_blank"
              rel="noopener noreferrer"
              className="text-cloudflare-orange hover:underline"
            >
              Report Generator spreadsheet
            </a>
          </li>
          <li>Paste the snippet in cell <strong>B2</strong> and follow instructions in the sheet</li>
          <li>Give approval to the script when first run (essential)</li>
        </ol>
      </div>

      <div className="mb-3">
        <label className="block text-xs font-semibold text-gray-700 mb-2">SNIPPET (Click to copy):</label>
        <div 
          className="relative bg-white border border-gray-300 rounded-md p-4 cursor-pointer hover:border-cloudflare-orange transition-colors"
          onClick={handleCopy}
        >
          <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all font-mono overflow-x-auto max-h-40">
            {generateSnippet()}
          </pre>
          
          {copied && (
            <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
              Copied!
            </div>
          )}
        </div>
      </div>

      <button
        onClick={handleCopy}
        className="w-full bg-cloudflare-orange text-white font-semibold py-2 px-4 rounded-md hover:bg-orange-600 transition-colors flex items-center justify-center gap-2"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
        Copy Snippet
      </button>

      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
        <p className="font-semibold mb-1">Next Steps:</p>
        <ul className="space-y-1 ml-4 list-disc">
          <li>
            Test your pitch with the{' '}
            <a href="https://gemini.google.com/gem/1how7IDCcJbpgCQHmZxqr83QSyPGncjXS?usp=sharing" target="_blank" rel="noopener noreferrer" className="underline">
              Role Play Coach
            </a>
          </li>
          <li>
            Prepare strategy with the{' '}
            <a href="https://gemini.google.com/gem/1kk-IXbzXFpfAdIkmQaZowG9zDcUWyGI2?usp=sharing" target="_blank" rel="noopener noreferrer" className="underline">
              Discovery Coach
            </a>
          </li>
          <li>Align your team (SE, CSM, Sales Manager) to the strategy</li>
        </ul>
      </div>
    </div>
  );
};
